import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, HeartHandshake, Sparkles, Clock, Download, Home } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <section className="warm-card p-6 md:p-8">
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl bg-hearth-clay/10 text-hearth-clayDark grid place-items-center shrink-0">
        <Icon size={20} />
      </div>
      <div className="flex-1">
        <h2 className="font-display text-2xl md:text-3xl text-hearth-ink mb-2">{title}</h2>
        <div className="text-hearth-mute leading-relaxed space-y-3">{children}</div>
      </div>
    </div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-hearth-paper">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 caption text-hearth-clayDark hover:text-hearth-ink mb-8"
        >
          <Home size={13} /> back to your home
        </Link>

        <header className="mb-10">
          <p className="caption text-hearth-clayDark mb-3">HearthHouse · Privacy</p>
          <h1
            data-testid="privacy-title"
            className="font-display text-4xl md:text-5xl lg:text-6xl text-hearth-ink leading-tight"
          >
            Your household is yours. Always.
          </h1>
          <p className="mt-4 text-lg md:text-xl text-hearth-mute leading-relaxed max-w-2xl">
            HearthHouse holds a lot of gentle, everyday details — your grocery list,
            the way you cook, the bills you keep track of. Here&apos;s exactly how
            we care for that, in plain language.
          </p>
        </header>

        <div className="space-y-4" data-testid="privacy-sections">
          <Section icon={Lock} title="Encrypted at rest">
            <p>
              Everything you save lives inside an encrypted database. Nobody at
              HearthHouse browses your grocery list or reads through your recipes
              for fun — the data is stored in a way that even we can&apos;t idly
              peek at.
            </p>
          </Section>

          <Section icon={ShieldCheck} title="Never sold, never shared">
            <p>
              We do not sell your household data to advertisers, brokers, or
              anyone else. Not now, not later. HearthHouse makes money by being
              a home you want to come back to — not by monetising you.
            </p>
          </Section>

          <Section icon={Sparkles} title="Used only to help you cook & plan">
            <p>
              When you ask for a recipe suggestion or a spending summary, we send
              the minimum context needed to the AI (pantry names, cuisine
              preferences, expense categories). We don&apos;t send your full
              history or your identity. The AI never learns your name, your email,
              or where you live.
            </p>
            <p>
              We use three carefully-chosen AI models — Anthropic Claude, Google
              Gemini, and OpenAI (for image scanning) — always via a
              privacy-respecting proxy. They receive prompts and return answers,
              and that&apos;s where the conversation ends.
            </p>
          </Section>

          <Section icon={Clock} title="Kept only as long as you keep us">
            <p>
              Your household data stays with us for exactly as long as you want.
              We don&apos;t auto-archive, we don&apos;t trickle-delete, we
              don&apos;t rotate you out. If you stop using HearthHouse, your data
              waits patiently until you come back — or until you delete it.
            </p>
            <p>
              We do keep a small operational log (login timestamps, error traces)
              for 30 days to help fix bugs and secure the account — that log is
              purged automatically.
            </p>
          </Section>

          <Section icon={Download} title="Export or delete, anytime">
            <p>
              You are the owner of your household&apos;s data. You can:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-1">
              <li>
                <span className="text-hearth-ink font-medium">Download everything</span>{" "}
                — a single JSON file of every list, recipe, bill, plant, pet, and
                setting we hold for your household. Owners visit{" "}
                <Link to="/household" className="text-hearth-clay underline">Household</Link>{" "}
                → &quot;Export data&quot;.
              </li>
              <li>
                <span className="text-hearth-ink font-medium">Delete the whole household</span>{" "}
                — one confirmation and every row scoped to your household, plus
                every member account attached to it, is permanently removed
                within seconds. There&apos;s no soft-delete, no shadow copy.
              </li>
            </ul>
          </Section>

          <Section icon={HeartHandshake} title="If something changes, you&apos;ll hear it first">
            <p>
              This is not a moving-target policy. If we ever meaningfully change
              how we handle your data, you&apos;ll get an email and a banner on
              the dashboard <span className="italic">before</span> the change
              takes effect — never after. You always have the option to export
              and leave.
            </p>
          </Section>
        </div>

        <footer className="mt-12 pt-8 border-t border-hearth-line text-center">
          <p className="font-display italic text-hearth-mute text-lg">
            &ldquo;Small things, cared for well.&rdquo;
          </p>
          <p className="mt-2 text-xs text-hearth-mute">
            Questions? Write to us at{" "}
            <a href="mailto:privacy@hearthouse.app" className="text-hearth-clay underline">
              privacy@hearthouse.app
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
